import React, { useCallback } from 'react';
import { FieldSetWrapper } from './FieldWrapper';
import useFieldApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import componentTypes from '@data-driven-forms/react-form-renderer/dist/cjs/component-types';
import clsx from 'clsx';

interface Option {
  value: any;
  label: string;
}
interface RadioProps {
  options: Option[];
  name: string;
  label: string;
  initialValue: boolean;
  onChange: (val: boolean | string) => void;
}

interface RadioOptionProps {
  name: string;
  label: string;
  value: any;
  input: any;
  onChange: (e?: any) => void;
  labelClassName?: string;
  checkboxClassName?: string;
  activeCheckBoxClassName?: string;
}

/* istanbul ignore next */ /* this code is not used or will be deleted in other branches */
function RadioOptionDdf({ name, label, value, initialValue, ...rest }: any) {
  const { input } = useFieldApi({
    name,
    type: 'radio',
    value,
    component: componentTypes.RADIO,
    initialValue
  });
  const onChange = useCallback(() => {
    const { handlers, action, getState, change } = rest;
    if (handlers && action) {
      handlers[action]({ value, getState, change, name });
    }
    input.onChange(value);
  }, [input, value]);
  return (
    <RadioOption
      name={name}
      label={label}
      value={value}
      input={input}
      onChange={onChange}
      labelClassName={rest?.labelClassName || ''}
    />
  );
}

export function RadioOption({
  name,
  label,
  value,
  input,
  onChange,
  labelClassName = '',
  checkboxClassName = '',
  activeCheckBoxClassName = ''
}: RadioOptionProps) {
  return (
    <label
      htmlFor={`${name}-${value}`}
      className={clsx('flex items-center cursor-pointer', labelClassName || 'focus-within:ring')}
      data-testid='radio-option'
    >
      <input
        {...input}
        onChange={onChange}
        value={value}
        id={`${name}-${value}`}
        className='hidden'
        data-testid={input['data-testid'] || 'radio-option-input'}
      />
      <span
        className={clsx(
          'inline-flex justify-center items-center rounded-full h-5 w-5 border mr-1 bg-white',
          checkboxClassName || 'border-gray-default'
        )}
        data-testid='radio-option-checkbox'
      >
        {input.checked && (
          <span className={clsx('inline-flex rounded-full h-3 w-3', activeCheckBoxClassName || 'bg-blackish')} />
        )}
      </span>
      <span className='font-effra'>{label}</span>
    </label>
  );
}
/* istanbul ignore next */ /* this code is not used or will be deleted in other branches */
export function RadioDdf({
  options,
  name,
  label,
  initialValue,
  wrapperClassName,
  skipError = false,
  skipLabel = false,
  ...rest
}: any) {
  return (
    <FieldSetWrapper label={label} name={name} className={wrapperClassName} skipError={skipError} skipLabel={skipLabel}>
      <div className='h-12 flex items-center space-x-3'>
        {options.map(({ value, label: optionLabel }: any) => (
          <RadioOptionDdf
            key={value}
            name={name}
            label={optionLabel}
            value={value}
            initialValue={initialValue}
            {...rest}
          />
        ))}
      </div>
    </FieldSetWrapper>
  );
}

export function Radio({ options, name, label, initialValue, onChange }: RadioProps) {
  return (
    <FieldSetWrapper label={label} name={name}>
      <div className='h-12 flex items-center space-x-3' data-testid='radio-option-group'>
        {options.map(({ value, label: optionLabel }) => {
          const input = {
            type: 'radio',
            checked: value === initialValue
          };
          return (
            <RadioOption
              key={value.toString()}
              name={name}
              label={optionLabel}
              value={value}
              input={input}
              onChange={() => onChange(value)}
            />
          );
        })}
      </div>
    </FieldSetWrapper>
  );
}
